import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ShoppingBag, Check, Download } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import api, { mediaUrl } from '../lib/api';
import { useCart } from '../context/CartContext';

const ShopPage = () => {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState('All');
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState({});

  useEffect(() => {
    api.get('/shop/products').then((res) => setProducts(res.data || [])).catch(() => setProducts([])).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ['All', ...Array.from(new Set(products.map((p) => p.category)))], [products]);
  const filtered = cat === 'All' ? products : products.filter((p) => p.category === cat);

  const handleAdd = (p) => { addItem(p, 1); setAdded((s) => ({ ...s, [p.id]: true })); setTimeout(() => setAdded((s) => ({ ...s, [p.id]: false })), 1600); };

  return (
    <div className="App" data-testid="page-shop">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[1300px] mx-auto px-6 lg:px-10 py-16">
          <div className="text-center">
            <div className="eyebrow mb-3">Our Eco-Store</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-burgundy leading-tight">Bring <span className="font-italic-display text-rose-italic">the Retreat</span> Home</h1>
            <p className="mt-5 text-mahogany/80 max-w-2xl mx-auto">Books, digital works and curated tools to support your journey — naturally.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mt-8" data-testid="shop-categories">
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`shop-cat-${c.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${cat === c ? 'bg-mahogany text-cream border-mahogany' : 'bg-white/70 text-mahogany/80 border-rose/50 hover:bg-rose-soft/40'}`}>{c}</button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center mt-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
          ) : filtered.length === 0 ? (
            <div className="mt-16 text-center text-mahogany/60" data-testid="shop-empty">No products yet. Please check back soon.</div>
          ) : (
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="shop-grid">
              {filtered.map((p) => (
                <article key={p.id} className="group" data-testid={`product-${p.slug}`}>
                  <Link to={`/product/${p.slug}`} className="relative block product-image-frame aspect-[3/4] bg-cream-2 rounded-2xl overflow-hidden">
                    <SmartImage src={mediaUrl(p.image)} alt={p.title} className="w-full h-full">
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/85 text-mahogany font-medium">{p.category}</span>
                        {p.product_type === 'digital' && <span className="text-[11px] uppercase tracking-widest px-2.5 py-1.5 rounded-full bg-mahogany text-cream flex items-center gap-1"><Download className="w-3 h-3" /> Digital</span>}
                      </div>
                    </SmartImage>
                  </Link>
                  <div className="mt-5 flex items-start justify-between gap-4">
                    <Link to={`/product/${p.slug}`}><h3 className="font-serif-display text-burgundy text-2xl leading-tight hover:text-rose-italic transition-colors">{p.title}</h3></Link>
                    <span className="text-mahogany text-lg font-medium whitespace-nowrap">${p.price.toFixed(2)}</span>
                  </div>
                  <button onClick={() => handleAdd(p)} className="mt-4 pill-btn" data-testid={`add-to-cart-${p.slug}`}>
                    {added[p.id] ? <>Added <Check className="w-4 h-4" /></> : <>Add to Cart <ShoppingBag className="w-4 h-4" /></>}
                  </button>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ShopPage;
